/**
 * Resizes an image file/blob to fit within a specified maximum dimension while maintaining its aspect ratio.
 *
 * @param {File | Blob} file - The image file or blob to be resized.
 * @param {number} [maxSize=100] - The maximum width or height of the output image in pixels.
 * @returns {Promise<string>} A promise that resolves to the resized image as a JPEG Data URL (base64 string).
 * @throws {Error} Throws an error if reading the file, loading the image, or getting the 2D context fails.
 */
export function resizeImage(file: File | Blob, maxSize: number = 100): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.onload = (e: ProgressEvent<FileReader>) => {
            const result = e.target?.result;
            if (typeof result !== 'string') {
                reject(new Error('Failed to convert file to data URL'));
                return;
            }

            const img = new Image();
            img.onerror = () => reject(new Error('Failed to load image'));
            img.onload = () => {
                const canvas = document.createElement('canvas');
                let w = img.width;
                let h = img.height;

                if (w > h) {
                    if (w > maxSize) {
                        h = (h * maxSize) / w;
                        w = maxSize;
                    }
                } else {
                    if (h > maxSize) {
                        w = (w * maxSize) / h;
                        h = maxSize;
                    }
                }

                canvas.width = w;
                canvas.height = h;

                const ctx = canvas.getContext('2d');
                if (!ctx) {
                    reject(new Error('Failed to get 2D context'));
                    return;
                }

                ctx.drawImage(img, 0, 0, w, h);
                resolve(canvas.toDataURL('image/jpeg', 0.85));
            };

            img.src = result;
        };

        reader.readAsDataURL(file);
    });
}
